import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCapability } from '@/contexts/CapabilityContext';
import { useVoice } from '@/contexts/VoiceContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft,
  Mic,
  Upload,
  CheckCircle2,
  Circle,
  AlertCircle,
  Play,
  Download,
  FileText,
  Settings2,
  ExternalLink,
  FolderOpen,
  Archive,
  Loader2
} from 'lucide-react';

const VoiceWorkspace = ({ instance, wizardData, onBack }) => {
  const { capabilities, transitionInstance, archiveInstance } = useCapability();
  const { createJob, getJob, validateDataset, configureTraining, generateNotebook, downloadNotebook, importResults } = useVoice();
  const [job, setJob] = useState(null);
  const [currentStep, setCurrentStep] = useState(wizardData ? 2 : 0); // Skip to config if wizard completed
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form states - pre-populate from wizard if available
  const [drivePath, setDrivePath] = useState('/content/drive/MyDrive/VoiceModeling');
  const [modelType, setModelType] = useState(wizardData?.intent === 'song-vocals' ? 'vits' : 'xtts');
  const [epochs, setEpochs] = useState(wizardData?.intent === 'song-vocals' ? 150 : 100);
  const [batchSize, setBatchSize] = useState(8);
  const [learningRate, setLearningRate] = useState(0.0001);
  const [checkpointPath, setCheckpointPath] = useState('');
  
  // Use wizard data for samples if available
  const [samples, setSamples] = useState(
    wizardData?.files?.map((f, i) => ({
      id: String(i + 1),
      filename: f.name,
      duration_seconds: f.duration || 10,
      sample_rate: 44100,
      transcript: '',
      validated: true,
      validation_issues: []
    })) || []
  );

  const capability = capabilities.find(c => c.id === instance.capability_id);
  const steps = capability?.preparation_steps || [];

  useEffect(() => {
    const initJob = async () => {
      if (!job && instance.id) {
        try {
          setLoading(true);
          const newJob = await createJob(instance.id);
          setJob(newJob);
          
          // If we have wizard data, auto-validate
          if (wizardData?.files?.length > 0) {
            const wizardSamples = wizardData.files.map((f, i) => ({
              id: String(i + 1),
              filename: f.name,
              duration_seconds: f.duration || 10,
              sample_rate: 44100,
              transcript: '',
              validated: true,
              validation_issues: []
            }));
            await validateDataset(newJob.id, wizardSamples);
          }
        } catch (err) {
          console.log('Job initialization:', err);
        } finally {
          setLoading(false);
        }
      }
    };
    initJob();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance.id]);

  const getStateColor = (state) => {
    const colors = {
      idle: 'bg-zinc-500',
      prepared: 'bg-amber-500',
      executing: 'bg-blue-500',
      completed: 'bg-emerald-500',
      failed: 'bg-red-500',
      archived: 'bg-zinc-700'
    };
    return colors[state] || 'bg-zinc-500';
  };

  const handleAddSamples = () => {
    // Simulate adding samples
    const newSamples = [
      { id: '1', filename: 'sample_001.wav', duration_seconds: 15.5, sample_rate: 22050, transcript: 'Hello, this is a test recording.', validated: true, validation_issues: [] },
      { id: '2', filename: 'sample_002.wav', duration_seconds: 22.3, sample_rate: 22050, transcript: 'The quick brown fox jumps over the lazy dog.', validated: true, validation_issues: [] },
      { id: '3', filename: 'sample_003.wav', duration_seconds: 18.7, sample_rate: 22050, transcript: 'Voice modeling requires clean audio samples.', validated: true, validation_issues: [] },
    ];
    setSamples(newSamples);
  };

  const handleValidateDataset = async () => {
    if (!job) return;
    setLoading(true);
    setError('');
    try {
      await validateDataset(job.id, samples);
      const updatedJob = await getJob(job.id);
      setJob(updatedJob);
      setCurrentStep(2);
    } catch (err) {
      setError('Failed to validate dataset');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigure = async () => {
    if (!job) return;
    setLoading(true);
    setError('');
    try {
      await configureTraining(job.id, {
        model_type: modelType,
        epochs,
        batch_size: batchSize,
        learning_rate: learningRate,
        save_checkpoints: true,
        checkpoint_interval: 25,
        speaker_embedding_dim: 512,
        output_format: 'pytorch'
      });
      const updatedJob = await getJob(job.id);
      setJob(updatedJob);
      setCurrentStep(3);
    } catch (err) {
      setError('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNotebook = async () => {
    if (!job) return;
    setLoading(true);
    setError('');
    try {
      await generateNotebook(job.id, drivePath);
      const updatedJob = await getJob(job.id);
      setJob(updatedJob);
      await transitionInstance(instance.id, 'prepared', 'Notebook generated');
      setCurrentStep(4);
    } catch (err) {
      setError('Failed to generate notebook');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadNotebook = () => {
    if (!job) return;
    downloadNotebook(job.id);
  };

  const handleMarkExecuting = async () => {
    try {
      await transitionInstance(instance.id, 'executing', 'User started Colab execution');
    } catch (err) {
      setError('Failed to update state');
    }
  };

  const handleImportResults = async () => {
    if (!job || !checkpointPath) return;
    setLoading(true);
    setError('');
    try {
      await importResults(job.id, checkpointPath, ['Training completed successfully']);
      await transitionInstance(instance.id, 'completed', 'Results imported');
      const updatedJob = await getJob(job.id);
      setJob(updatedJob);
    } catch (err) {
      setError('Failed to import results');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    try {
      await archiveInstance(instance.id);
      onBack();
    } catch (err) {
      setError('Failed to archive');
    }
  };

  const progress = Math.round((currentStep / steps.length) * 100);

  return (
    <div className="min-h-screen bg-[#0a0a0b]" data-testid="voice-workspace">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBack}
                className="text-zinc-400 hover:text-zinc-100"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <Separator orientation="vertical" className="h-4 bg-zinc-700" />
              <div className="flex items-center gap-2">
                <Mic className="w-4 h-4 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-100">{capability?.name}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`${getStateColor(instance.state)} text-white text-xs`}>
                {instance.state}
              </Badge>
              <span className="text-xs text-zinc-500">{instance.id.slice(0, 8)}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <Alert variant="destructive" className="mb-6 bg-red-950/50 border-red-900">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Progress */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-zinc-400">Preparation Progress</span>
              <span className="text-sm text-zinc-300">{progress}%</span>
            </div>
            <Progress value={progress} className="h-1.5 bg-zinc-800" />
            <div className="flex justify-between mt-4">
              {steps.map((step, idx) => (
                <div key={step.order} className="flex flex-col items-center">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    idx < currentStep ? 'bg-emerald-600 text-white' :
                    idx === currentStep ? 'bg-zinc-700 text-zinc-100 ring-2 ring-zinc-600' :
                    'bg-zinc-800 text-zinc-500'
                  }`}>
                    {idx < currentStep ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                  </div>
                  <span className="text-xs text-zinc-500 mt-1 hidden sm:block">{step.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Workspace */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="workflow" className="w-full">
              <TabsList className="bg-zinc-800/50 mb-4">
                <TabsTrigger value="workflow" className="data-[state=active]:bg-zinc-700">Workflow</TabsTrigger>
                <TabsTrigger value="config" className="data-[state=active]:bg-zinc-700">Configuration</TabsTrigger>
                <TabsTrigger value="outputs" className="data-[state=active]:bg-zinc-700">Outputs</TabsTrigger>
              </TabsList>

              <TabsContent value="workflow">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-6">
                    {/* Step 1: Dataset Upload */}
                    {currentStep === 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <Upload className="w-5 h-5 text-zinc-400" />
                          <h3 className="text-lg font-medium text-zinc-100">Dataset Upload</h3>
                        </div>
                        <p className="text-sm text-zinc-500">
                          Upload audio samples and optional transcripts for training.
                        </p>
                        <div className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center">
                          <Upload className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
                          <p className="text-sm text-zinc-400 mb-3">Drag and drop audio files here, or click to browse</p>
                          <Button 
                            variant="outline" 
                            onClick={handleAddSamples}
                            className="border-zinc-700 text-zinc-300"
                            data-testid="add-samples-btn"
                          >
                            Add Sample Files
                          </Button>
                        </div>
                        {samples.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm text-zinc-400">{samples.length} samples added</p>
                            <Button onClick={() => setCurrentStep(1)} data-testid="continue-to-validate">
                              Continue to Validation
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Step 2: Validation */}
                    {currentStep === 1 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-zinc-400" />
                          <h3 className="text-lg font-medium text-zinc-100">Dataset Validation</h3>
                        </div>
                        <p className="text-sm text-zinc-500">
                          Validating audio quality, duration, and format.
                        </p>
                        <ScrollArea className="h-48 rounded border border-zinc-800 p-3">
                          {samples.map((sample) => (
                            <div key={sample.id} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-zinc-500" />
                                <span className="text-sm text-zinc-300">{sample.filename}</span>
                              </div>
                              <span className="text-xs text-zinc-500">{sample.duration_seconds.toFixed(1)}s</span>
                            </div>
                          ))}
                        </ScrollArea>
                        <Button onClick={handleValidateDataset} disabled={loading} data-testid="validate-dataset-btn">
                          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Validate Dataset
                        </Button>
                      </div>
                    )}

                    {/* Step 3: Configuration */}
                    {currentStep === 2 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <Settings2 className="w-5 h-5 text-zinc-400" />
                          <h3 className="text-lg font-medium text-zinc-100">Training Configuration</h3>
                        </div>
                        <div className="grid gap-4">
                          <div>
                            <Label className="text-zinc-400">Model Type</Label>
                            <Select value={modelType} onValueChange={setModelType}>
                              <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-zinc-100">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-zinc-700">
                                <SelectItem value="xtts">XTTS (Recommended)</SelectItem>
                                <SelectItem value="vits">VITS</SelectItem>
                                <SelectItem value="custom">Custom</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-zinc-400">Epochs</Label>
                            <Input 
                              type="number" 
                              value={epochs} 
                              onChange={(e) => setEpochs(parseInt(e.target.value))}
                              className="bg-zinc-800/50 border-zinc-700 text-zinc-100"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-400">Batch Size</Label>
                            <Input 
                              type="number" 
                              value={batchSize} 
                              onChange={(e) => setBatchSize(parseInt(e.target.value))}
                              className="bg-zinc-800/50 border-zinc-700 text-zinc-100"
                            />
                          </div>
                        </div>
                        <Button onClick={handleConfigure} disabled={loading} data-testid="save-config-btn">
                          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Save Configuration
                        </Button>
                      </div>
                    )}

                    {/* Step 4: Notebook Generation */}
                    {currentStep === 3 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-zinc-400" />
                          <h3 className="text-lg font-medium text-zinc-100">Notebook Generation</h3>
                        </div>
                        <p className="text-sm text-zinc-500">
                          Generate a Google Colab notebook for training.
                        </p>
                        <div>
                          <Label className="text-zinc-400">Google Drive Path</Label>
                          <Input 
                            value={drivePath} 
                            onChange={(e) => setDrivePath(e.target.value)}
                            className="bg-zinc-800/50 border-zinc-700 text-zinc-100"
                            placeholder="/content/drive/MyDrive/VoiceModeling"
                          />
                        </div>
                        <Button onClick={handleGenerateNotebook} disabled={loading} data-testid="generate-notebook-btn">
                          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Generate Notebook
                        </Button>
                      </div>
                    )}

                    {/* Step 5: Execution */}
                    {currentStep >= 4 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <Play className="w-5 h-5 text-emerald-500" />
                          <h3 className="text-lg font-medium text-zinc-100">Ready for Execution</h3>
                        </div>
                        <p className="text-sm text-zinc-500">
                          Your notebook is ready. Download and run in Google Colab.
                        </p>
                        <div className="flex gap-3">
                          <Button onClick={handleDownloadNotebook} variant="outline" className="border-zinc-700" data-testid="download-notebook-btn">
                            <Download className="w-4 h-4 mr-2" />
                            Download Notebook
                          </Button>
                          <Button 
                            onClick={() => window.open('https://colab.research.google.com', '_blank')}
                            variant="outline"
                            className="border-zinc-700"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open Colab
                          </Button>
                        </div>
                        {instance.state === 'prepared' && (
                          <Button onClick={handleMarkExecuting} className="bg-blue-600 hover:bg-blue-700">
                            Mark as Executing
                          </Button>
                        )}
                        {instance.state === 'executing' && (
                          <div className="pt-4 border-t border-zinc-800 space-y-3">
                            <h4 className="text-sm font-medium text-zinc-200">Import Results</h4>
                            <Input 
                              value={checkpointPath}
                              onChange={(e) => setCheckpointPath(e.target.value)}
                              placeholder="/content/drive/MyDrive/VoiceModeling/output/model.pt"
                              className="bg-zinc-800/50 border-zinc-700 text-zinc-100"
                            />
                            <Button onClick={handleImportResults} disabled={loading || !checkpointPath} data-testid="import-results-btn">
                              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                              Import Results
                            </Button>
                          </div>
                        )}
                        {instance.state === 'completed' && (
                          <Alert className="bg-emerald-950/50 border-emerald-800">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            <AlertDescription className="text-emerald-300">
                              Training completed! Model checkpoint imported successfully.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="config">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-zinc-100">Training Configuration</CardTitle>
                    <CardDescription className="text-zinc-500">Current training parameters</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Model Type</span>
                        <span className="text-zinc-200">{job?.training_config?.model_type || modelType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Epochs</span>
                        <span className="text-zinc-200">{job?.training_config?.epochs || epochs}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Batch Size</span>
                        <span className="text-zinc-200">{job?.training_config?.batch_size || batchSize}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Learning Rate</span>
                        <span className="text-zinc-200">{job?.training_config?.learning_rate || learningRate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Drive Path</span>
                        <span className="text-zinc-200 text-right">{drivePath}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="outputs">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-zinc-100">Outputs</CardTitle>
                    <CardDescription className="text-zinc-500">Generated artifacts and results</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {job?.colab_notebook ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 rounded bg-zinc-800/50 border border-zinc-700">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-zinc-500" />
                            <span className="text-sm text-zinc-200">voice_training_{job.id.slice(0, 8)}.ipynb</span>
                          </div>
                          <Button size="sm" variant="ghost" onClick={handleDownloadNotebook}>
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                        {job.final_model_path && (
                          <div className="flex items-center justify-between p-3 rounded bg-zinc-800/50 border border-zinc-700">
                            <div className="flex items-center gap-2">
                              <FolderOpen className="w-4 h-4 text-emerald-500" />
                              <span className="text-sm text-zinc-200">Model Checkpoint</span>
                            </div>
                            <span className="text-xs text-zinc-500">{job.final_model_path}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-zinc-600 text-center py-8">
                        No outputs generated yet
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Instance Info */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-100">Instance Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">ID</span>
                  <span className="text-zinc-300 font-mono text-xs">{instance.id.slice(0, 12)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">State</span>
                  <Badge className={`${getStateColor(instance.state)} text-white text-xs`}>
                    {instance.state}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Created</span>
                  <span className="text-zinc-300 text-xs">
                    {new Date(instance.created_at).toLocaleDateString()}
                  </span>
                </div>
                {instance.started_at && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Started</span>
                    <span className="text-zinc-300 text-xs">
                      {new Date(instance.started_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* State History */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-100">State History</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-40">
                  {instance.state_history.length === 0 ? (
                    <p className="text-xs text-zinc-600 text-center py-4">No transitions yet</p>
                  ) : (
                    <div className="space-y-2">
                      {instance.state_history.map((entry, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs">
                          <Circle className="w-2 h-2 mt-1.5 text-zinc-600" />
                          <div>
                            <span className="text-zinc-400">{entry.from_state}</span>
                            <span className="text-zinc-600"> → </span>
                            <span className="text-zinc-300">{entry.to_state}</span>
                            {entry.message && (
                              <p className="text-zinc-500 mt-0.5">{entry.message}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-100">Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleArchive}
                  className="w-full justify-start text-zinc-400 hover:text-red-400 hover:bg-red-950/50"
                  data-testid="archive-btn"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Instance
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VoiceWorkspace;
